import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import Icon from '../../../components/foundations/Icon';
import { buildImportFormatRows } from '../../utils';
import UploadView from './UploadView';
import ResultView from './ResultView';
import { IMPORT_MODAL_PREFIX as PREFIX, importResultShape } from './consts';
import css from './ImportModal.css';

// Two-view CSV import modal: an upload view (overwrite warning, dynamic expected-format table,
// dropzone) and a result view (success count or per-row errors + downloadable error report).
const ImportModal = ({
    open,
    translate,
    visibleColumns,
    isValueCurrencyEnabled,
    isEventRequired,
    helpLink,
    uploading,
    result,
    onImport,
    onClose,
    onBackToUpload,
}) => {
    const [file, setFile] = useState(null);

    const formatRows = useMemo(
        () => buildImportFormatRows({ visibleColumns, isValueCurrencyEnabled, isEventRequired }),
        [visibleColumns, isValueCurrencyEnabled, isEventRequired]
    );

    const showResult = Boolean(result);

    // Block dismissal (backdrop / Escape / ×) while an import is in flight so its result isn't lost,
    // and always drop the staged file on close so a reopen can't silently re-submit it.
    const handleClose = () => {
        if (uploading) {
            return;
        }
        setFile(null);
        onClose();
    };

    const handleBack = () => {
        setFile(null);
        onBackToUpload();
    };

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={{ className: css.paper }}>
            <div className={css.header}>
                <div>
                    <h2 className={css.title}>{translate(showResult ? `${PREFIX}.RESULT_TITLE` : `${PREFIX}.TITLE`)}</h2>
                    <p className={css.subtitle}>
                        {showResult && result.fileName ? result.fileName : translate(`${PREFIX}.SUBTITLE`)}
                    </p>
                </div>
                <button
                    type="button"
                    className={css.closeButton}
                    aria-label={translate(`${PREFIX}.CLOSE`)}
                    disabled={uploading}
                    onClick={handleClose}
                >
                    <Icon name="x" interactive={false} />
                </button>
            </div>
            {showResult ? (
                <ResultView translate={translate} result={result} onBackToUpload={handleBack} onClose={handleClose} />
            ) : (
                <UploadView
                    translate={translate}
                    formatRows={formatRows}
                    isEventRequired={isEventRequired}
                    helpLink={helpLink}
                    file={file}
                    uploading={uploading}
                    onFileChange={setFile}
                    onClearFile={() => setFile(null)}
                    onSubmit={() => file && onImport(file)}
                    onClose={handleClose}
                />
            )}
        </Dialog>
    );
};

ImportModal.propTypes = {
    open: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
    visibleColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    isValueCurrencyEnabled: PropTypes.bool,
    isEventRequired: PropTypes.bool,
    helpLink: PropTypes.string,
    uploading: PropTypes.bool,
    result: importResultShape,
    onImport: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onBackToUpload: PropTypes.func.isRequired,
};

export default ImportModal;
